import { z } from 'zod'
import { contractServiceOptions } from './contract'

/**
 * Paramètres du Contrat de déneigement résidentiel.
 *
 * Une **saison** porte des **versions**. Une version est l'état complet des
 * réglages à un instant donné; elle n'est jamais modifiée après son activation,
 * seulement remplacée par la suivante. Un Contrat signé retient la version qu'il
 * a acceptée : c'est ce qui rend son document reconstructible à l'identique.
 *
 * Les textes de référence sont fournis par le propriétaire pour servir de base
 * aux nouvelles versions de saison. Une version activée reste immuable : une
 * correction future ne réécrit jamais un Contrat déjà signé.
 */

export const contractSettingsSeasonStatuses = ['DRAFT', 'ACTIVE', 'ARCHIVED'] as const
export type ContractSettingsSeasonStatus = (typeof contractSettingsSeasonStatuses)[number]

export const contractSettingsVersionStatuses = ['ACTIVE', 'RETIRED'] as const
export type ContractSettingsVersionStatus = (typeof contractSettingsVersionStatuses)[number]

export interface ContractSettingsGeneral {
  legalName: string
  address: string
  phone: string
  email: string
  contractDisplayName: string
}

export interface ContractSettingsService {
  /** Saisi tel qu'il doit apparaître au document, unité comprise. */
  triggerThresholdCm: string
  /** Texte complet de la carte « Seuil d'intervention » (page 1). */
  triggerThresholdText: string
  /** Réserve climatique imprimée en page 1 sous les délais. */
  specialConditions: string
  /** Début de la période de service affichée, ISO (AAAA-MM-JJ). */
  servicePeriodStart: string
  /** Fin de la période de service affichée, ISO (AAAA-MM-JJ). */
  servicePeriodEnd: string
  /** Clés de `contractServiceOptions` proposées dans l'assistant nouveau contrat. */
  enabledServiceKeys: string[]
}

/** Registre fermé du mode de dépôt de la neige par défaut. */
export const snowDepositModes = ['PROPRIETE_CLIENT', 'AUTRE'] as const
export type SnowDepositMode = (typeof snowDepositModes)[number]

export const snowDepositModeLabels: Record<SnowDepositMode, string> = {
  PROPRIETE_CLIENT: 'Sur le terrain du client',
  AUTRE: 'Autres',
}

/** Registre fermé des modes de paiement configurables. */
export const paymentMethodKeys = ['INTERAC', 'CARTE_CREDIT', 'CHEQUE', 'COMPTANT'] as const
export type PaymentMethodKey = (typeof paymentMethodKeys)[number]

export const paymentMethodLabels: Record<PaymentMethodKey, string> = {
  INTERAC: 'Virement Interac',
  CARTE_CREDIT: 'Carte de crédit',
  CHEQUE: 'Chèque',
  COMPTANT: 'Comptant',
}

export interface ContractSettingsOperations {
  snowDepositMode: SnowDepositMode
  /** Affiché seulement si `snowDepositMode === 'AUTRE'`. */
  snowDepositCustomText: string
  /** Repli imprimé quand le Contrat n'ajoute pas d'obligation propre. */
  defaultClientObligations: string
  /** Repli imprimé quand le Contrat n'ajoute pas d'exclusion propre. */
  defaultExclusions: string
}

export interface ContractSettingsPricing {
  paymentMethods: PaymentMethodKey[]
  /** Note en italique sous l'échéancier, ex. « Le paiement peut être effectué par… ». */
  paymentMethodsNote: string
  /** Échéance par défaut du versement « Paiement mi-saison », ISO (AAAA-MM-JJ). */
  midSeasonPaymentDate: string
}

export interface ContractSettingsDocument {
  /** Paragraphe du bloc Acceptation de la page 1. */
  acceptanceText: string
  /** Paragraphe du bloc Acceptation des conditions générales de la page 2. */
  generalTermsAcceptanceText: string
  /** Phrase de remerciement du pied de page. */
  footerThanksText: string
  /** Texte de la boîte de remerciement de la page 2. */
  thanksBoxText: string
  /** Puces de la carte Obligations clés de la page 1. */
  keyObligations: string[]
}

export interface ContractSettingsClause {
  key: string
  title: string
  body: string
  enabled: boolean
  isLegal: boolean
  order: number
}

/**
 * Gabarit de clause : le texte de référence de l'organisation, hors saison.
 *
 * Un gabarit sert de point de départ aux clauses d'une saison neuve, et rien
 * de plus. Le modifier ne change aucune version déjà créée, donc aucun Contrat
 * déjà signé : les clauses d'une version vivent leur vie une fois composées.
 */
export interface ContractClauseTemplate {
  id: string
  key: string
  title: string
  body: string
  isLegal: boolean
  displayOrder: number
}

export interface UpsertContractClauseTemplateCommand {
  key: string
  title: string
  body: string
  isLegal: boolean
}

export interface ContractSettingsVersion {
  id: string
  seasonId: string
  version: number
  status: ContractSettingsVersionStatus
  general: ContractSettingsGeneral
  service: ContractSettingsService
  operations: ContractSettingsOperations
  pricing: ContractSettingsPricing
  document: ContractSettingsDocument
  clauses: ContractSettingsClause[]
  activatedAt: string
}

export interface ContractSettingsSeason {
  id: string
  label: string
  status: ContractSettingsSeasonStatus
  createdAt: string
  versions: ContractSettingsVersion[]
}

export interface ContractSettingsResult {
  seasons: ContractSettingsSeason[]
  meta: {
    source: 'supabase' | 'fixtures' | 'fixture-fallback'
    retrievedAt: string
    fallbackReason?: string
  }
}

export interface ActivateContractSettingsVersionCommand {
  idempotencyKey: string
  seasonId: string
  general: ContractSettingsGeneral
  service: ContractSettingsService
  operations: ContractSettingsOperations
  pricing: ContractSettingsPricing
  document: ContractSettingsDocument
  clauses: ContractSettingsClause[]
}

export interface ActivateContractSettingsVersionResult {
  versionId: string
  seasonId: string
  version: number
  status: ContractSettingsVersionStatus
  activatedAt: string
}

export interface ContractSettingsSeasonResult {
  seasonId: string
  label: string
  status: ContractSettingsSeasonStatus
}

export const emptyContractSettingsGeneral: ContractSettingsGeneral = {
  legalName: '',
  address: '',
  phone: '',
  email: '',
  contractDisplayName: '',
}

export const defaultEnabledServiceKeys: string[] = contractServiceOptions.map((option) => option.key)

export const emptyContractSettingsService: ContractSettingsService = {
  triggerThresholdCm: '',
  triggerThresholdText: '',
  specialConditions: '',
  servicePeriodStart: '',
  servicePeriodEnd: '',
  enabledServiceKeys: defaultEnabledServiceKeys,
}

export const emptyContractSettingsOperations: ContractSettingsOperations = {
  snowDepositMode: 'PROPRIETE_CLIENT',
  snowDepositCustomText: '',
  defaultClientObligations: '',
  defaultExclusions: '',
}

export const emptyContractSettingsPricing: ContractSettingsPricing = {
  paymentMethods: [],
  paymentMethodsNote: '',
  midSeasonPaymentDate: '',
}

/**
 * Textes imprimés par défaut quand aucune saison n'en fournit de propres —
 * seule source de vérité, reprise telle quelle par `contractDocumentModel.ts`
 * au rendu. Avant cette version, ces textes n'existaient qu'en dur dans le
 * gabarit PDF : l'écran Paramètres semblait vide alors que le contrat réel
 * imprimait déjà quelque chose, sans qu'on puisse voir quoi ni le modifier.
 */
export const defaultContractAcceptanceText =
  'En signant, le Client confirme avoir pris connaissance du présent contrat, incluant les conditions générales présentées à la page 2, et en accepte l’ensemble des modalités.'
export const defaultContractGeneralTermsAcceptanceText =
  'En signant,\nle Client confirme avoir pris connaissance des conditions générales ci-dessus et en accepte l’ensemble des modalités.'
export const defaultContractThanksBoxText =
  'Nous apprécions la confiance que vous nous accordez pour toute la rigueur de la saison hivernale.'
export const defaultContractFooterThanksText =
  'Nous mettons tout en œuvre pour vous offrir un service fiable et sécuritaire tout l’hiver.'
export const defaultContractKeyObligations = [
  'Groupe RÉCA met tout en œuvre pour offrir un service fiable et sécuritaire.',
  'Le Client doit maintenir un accès dégagé aux surfaces à entretenir.',
]

/**
 * Phrase complète de la carte « Seuil d'intervention » (page 1), générée à
 * partir du seuil configuré — sert de valeur de départ modifiable, pas d'un
 * gabarit figé : une fois éditée, `triggerThresholdText` prime.
 */
export function defaultTriggerThresholdText(triggerThresholdCm: string): string {
  const cm = triggerThresholdCm.trim()
  if (cm === '') return ''
  return `Les services se déclenchent lorsque l’accumulation de neige atteint ou dépasse ${cm}.`
}

/**
 * Même repli que `withDocumentDefaults`, pour les deux phrases complètes de
 * la section Service : une version déjà activée avant l'ajout de ces champs
 * n'a que le seuil/l'heure bruts, jamais la phrase toute faite.
 */
export function withServiceDefaults(service: ContractSettingsService): ContractSettingsService {
  return {
    ...service,
    triggerThresholdText:
      service.triggerThresholdText.trim() || defaultTriggerThresholdText(service.triggerThresholdCm),
  }
}

/**
 * Une version déjà activée avec ces champs laissés vides (avant l'ajout des
 * valeurs par défaut ci-dessus) stocke des chaînes vides, pas des clés
 * absentes : le schéma Zod ne les remplace donc pas tout seul. Cette
 * fonction comble les champs vides d'une version chargée avec le même texte
 * que celui déjà imprimé sur le contrat, pour que l'écran Paramètres montre
 * toujours ce qui sort réellement du PDF plutôt qu'une case vide trompeuse.
 */
export function withDocumentDefaults(document: ContractSettingsDocument): ContractSettingsDocument {
  return {
    acceptanceText: document.acceptanceText.trim() || defaultContractAcceptanceText,
    generalTermsAcceptanceText:
      document.generalTermsAcceptanceText.trim() || defaultContractGeneralTermsAcceptanceText,
    footerThanksText: document.footerThanksText.trim() || defaultContractFooterThanksText,
    thanksBoxText: document.thanksBoxText.trim() || defaultContractThanksBoxText,
    keyObligations:
      document.keyObligations.length > 0 ? document.keyObligations : defaultContractKeyObligations,
  }
}

export const emptyContractSettingsDocument: ContractSettingsDocument = {
  acceptanceText: defaultContractAcceptanceText,
  generalTermsAcceptanceText: defaultContractGeneralTermsAcceptanceText,
  footerThanksText: defaultContractFooterThanksText,
  thanksBoxText: defaultContractThanksBoxText,
  keyObligations: defaultContractKeyObligations,
}

/**
 * Les douze conditions générales imprimées en page 2 du Contrat.
 *
 * Miroir exact de `contract_default_clauses_v1()`
 * (`supabase/migrations/20260821030000_contract_clause_v3_consolidation.sql`) :
 * l'écran Paramètres amorce l'onglet Clauses depuis cette liste, le serveur
 * complète depuis la sienne, et les deux doivent dire la même chose.
 */
export const defaultContractSettingsClauses: ContractSettingsClause[] = [
  {
    key: 'objet_du_contrat',
    title: 'Objet du contrat',
    body: 'Le présent Contrat a pour objet la fourniture, aux adresses de service indiquées à la page 1, des seuls services qui y sont énumérés. Il s’applique entre les dates fixes indiquées à la page 1 et prend fin à la dernière de ces dates. Il ne se renouvelle pas automatiquement : toute saison suivante fait l’objet d’une nouvelle proposition écrite que le Client demeure libre d’accepter. Le Contrat comprend la page 1, les présentes conditions, les configurations propres à chaque adresse et les annexes. Il est remis en français et régi par les lois applicables au Québec.',
    isLegal: true,
  },
  {
    key: 'seuil_intervention',
    title: 'Seuil d’intervention',
    body: 'Le déneigement est déclenché lorsque l’accumulation atteint ou dépasse le seuil précis indiqué à la page 1. Une accumulation moindre n’entraîne pas automatiquement un passage, sauf entente écrite entre les parties.',
    isLegal: false,
  },
  {
    key: 'execution_du_service',
    title: 'Exécution du service',
    body: 'Les opérations sont exécutées avec prudence, selon l’accumulation, la sécurité, l’accessibilité et les routes de service établies par Groupe RÉCA. Lors de précipitations prolongées, plusieurs passages peuvent être requis; le nettoyage final, lorsqu’il est inclus, suit la fin de la précipitation et le passage des équipements municipaux. Le bourrelet laissé par la charrue municipale n’est retiré que si ce service est indiqué comme inclus. Sur une surface en gravier ou non gelée, la méthode est adaptée et aucun résultat de chaussée nue n’est garanti.',
    isLegal: false,
  },
  {
    key: 'acces_et_vehicules',
    title: 'Accès et véhicules',
    body: 'Le Client maintient les accès libres, déplace les véhicules pendant la période communiquée, signale les risques et fournit des coordonnées à jour. Une surface bloquée par un véhicule, une remorque, une poubelle ou tout autre objet peut être omise pour éviter tout dommage; elle est reprise selon la prochaine disponibilité opérationnelle. Le Client communique rapidement toute condition pouvant modifier l’exécution sécuritaire du service.',
    isLegal: false,
  },
  {
    key: 'depot_de_la_neige',
    title: 'Dépôt de la neige',
    body: 'La neige est déposée uniquement à l’endroit indiqué à la page 1 ou dans les instructions propres à l’adresse. Le dépôt demeure assujetti aux règlements municipaux applicables et aux droits des tiers. Le Client indique un emplacement autorisé; aucun dépôt sur la voie publique ou chez un tiers n’est permis sans autorisation.',
    isLegal: false,
  },
  {
    key: 'obstacles_et_installations',
    title: 'Obstacles et installations',
    body: 'Le Client identifie clairement bordures, marches, drains, fils, décorations et autres obstacles non visibles sous la neige. Groupe RÉCA prend les précautions raisonnables, sans exclure la responsabilité que la loi lui impose, et n’est pas responsable des dommages causés à des éléments non signalés ou non visibles. Le déneigement ne garantit pas une surface sans glace : sauf inclusion expresse du déglaçage ou des abrasifs, le Client demeure responsable des précautions nécessaires sur les surfaces glissantes.',
    isLegal: true,
  },
  {
    key: 'services_non_inclus',
    title: 'Services non inclus',
    body: 'Tout service absent de la page 1 est exclu, notamment le déglaçage, l’application de sel ou d’abrasifs, le déneigement des toitures, escaliers, balcons et stationnements additionnels non prévus, le transport de la neige et tout travail manuel lorsqu’ils ne sont pas expressément sélectionnés.',
    isLegal: false,
  },
  {
    key: 'prix_et_paiements',
    title: 'Prix et paiements',
    body: 'Le prix total et les taxes figurent à la page 1. Le prix d’un Contrat saisonnier à durée déterminée ne peut être augmenté unilatéralement; tout travail supplémentaire exige une entente expresse. Le Client paie les montants aux dates et selon le mode indiqués à la page 1, et aucun frais non chiffré ne peut être réclamé. À distance ou en commerce itinérant, les restrictions légales sur les paiements anticipés s’appliquent. Toute modification d’un élément essentiel exige l’accord exprès des parties, consigné sur un support pouvant être conservé.',
    isLegal: true,
  },
  {
    key: 'annulation',
    title: 'Annulation',
    body: 'Le Client conserve ses droits légaux de résolution et de résiliation; toute demande d’annulation est transmise par écrit. Pour un Contrat conclu à distance, il peut notamment y mettre fin si le service n’est pas fourni dans les 30 jours suivant la date prévue, si aucun exemplaire du Contrat ne lui a été transmis dans les 15 jours suivant sa conclusion, ou dans les 7 jours suivant la constatation de renseignements manquants; les sommes payées lui sont alors remboursées dans les 15 jours. En commerce itinérant, le délai de résolution de 10 jours s’applique. Groupe RÉCA peut suspendre une opération devenue dangereuse ou inaccessible et en avise le Client.',
    isLegal: true,
  },
  {
    key: 'responsabilite',
    title: 'Responsabilité',
    body: 'Groupe RÉCA exécute les services avec les précautions raisonnablement attendues d’une entreprise de déneigement. Chaque partie répond de sa faute et de ses obligations, et aucune disposition n’exclut les garanties, recours ou responsabilités auxquels le Client ne peut valablement renoncer. Le Client signale rapidement tout dommage avec les renseignements utiles et permet une inspection raisonnable, sans que cette démarche ne réduise aucun délai ni recours prévu par la loi.',
    isLegal: true,
  },
  {
    key: 'communications',
    title: 'Communications',
    body: 'Les communications opérationnelles utilisent les coordonnées inscrites au dossier; chaque partie avise l’autre de tout changement et un avis juridique est transmis selon la forme exigée par la loi. Avant de conclure, le Client peut examiner la proposition, corriger ses renseignements, l’accepter ou la refuser. La preuve d’acceptation conserve le signataire, le moment, la méthode et la version acceptée; un exemplaire durable lui est transmis dans les 15 jours suivant la conclusion.',
    isLegal: true,
  },
  {
    key: 'evenements_hors_controle',
    title: 'Événements hors contrôle',
    body: 'Une obligation peut être retardée pendant un événement imprévisible et irrésistible hors du contrôle raisonnable de la partie touchée, notamment une tempête majeure, une fermeture de route, une panne d’équipement ou des conditions climatiques extrêmes. Celle-ci en avise l’autre et reprend l’exécution dès que possible.',
    isLegal: true,
  },
].map((clause, index) => ({ ...clause, enabled: true, order: index + 1 }))

const generalSchema = z.object({
  legalName: z.string().default(''),
  address: z.string().default(''),
  phone: z.string().default(''),
  email: z.string().default(''),
  contractDisplayName: z.string().default(''),
})

const serviceSchema = z.object({
  triggerThresholdCm: z.string().default(''),
  triggerThresholdText: z.string().default(''),
  specialConditions: z.string().default(''),
  servicePeriodStart: z.string().default(''),
  servicePeriodEnd: z.string().default(''),
  enabledServiceKeys: z.array(z.string()).default(defaultEnabledServiceKeys),
})

const operationsSchema = z.object({
  snowDepositMode: z.enum(snowDepositModes).default('PROPRIETE_CLIENT'),
  snowDepositCustomText: z.string().default(''),
  defaultClientObligations: z.string().default(''),
  defaultExclusions: z.string().default(''),
})

const pricingSchema = z.object({
  paymentMethods: z.array(z.enum(paymentMethodKeys)).default([]),
  paymentMethodsNote: z.string().default(''),
  midSeasonPaymentDate: z.string().default(''),
})

const documentSchema = z.object({
  acceptanceText: z.string().default(defaultContractAcceptanceText),
  generalTermsAcceptanceText: z.string().default(defaultContractGeneralTermsAcceptanceText),
  footerThanksText: z.string().default(defaultContractFooterThanksText),
  thanksBoxText: z.string().default(defaultContractThanksBoxText),
  keyObligations: z.array(z.string()).default(defaultContractKeyObligations),
})

const clauseSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  body: z.string().default(''),
  enabled: z.boolean().default(true),
  isLegal: z.boolean().default(false),
  order: z.number().int().min(1),
})

export const contractSettingsSeasonRowSchema = z.object({
  id: z.uuid(),
  label: z.string().min(1),
  status: z.enum(contractSettingsSeasonStatuses),
  created_at: z.string(),
})

export const contractSettingsVersionRowSchema = z.object({
  id: z.uuid(),
  season_id: z.uuid(),
  version: z.number().int().positive(),
  status: z.enum(contractSettingsVersionStatuses),
  general: generalSchema.catch(emptyContractSettingsGeneral),
  service: serviceSchema.catch(emptyContractSettingsService),
  operations: operationsSchema.catch(emptyContractSettingsOperations),
  pricing: pricingSchema.catch(emptyContractSettingsPricing),
  document: documentSchema.catch(emptyContractSettingsDocument),
  clauses: z.array(clauseSchema).catch([]),
  activated_at: z.string(),
})

export const contractClauseTemplateRowSchema = z.object({
  id: z.uuid(),
  key: z.string().min(1),
  title: z.string().min(1),
  body: z.string().default(''),
  is_legal: z.boolean(),
  display_order: z.number().int().min(1),
})

export const activateContractSettingsVersionResultSchema = z.object({
  versionId: z.uuid(),
  seasonId: z.uuid(),
  version: z.number().int().positive(),
  status: z.enum(contractSettingsVersionStatuses),
  activatedAt: z.string(),
})

export const contractSettingsSeasonResultSchema = z.object({
  seasonId: z.uuid(),
  label: z.string().min(1),
  status: z.enum(contractSettingsSeasonStatuses),
})

/** Format canonique déjà imposé à `contracts.saison`. */
export function isContractSeasonLabel(value: string): boolean {
  return /^[0-9]{4}-[0-9]{4}$/.test(value.trim())
}

export function activeContractSettingsVersion(
  season: ContractSettingsSeason | undefined,
): ContractSettingsVersion | undefined {
  return season?.versions.find((version) => version.status === 'ACTIVE')
}

/**
 * Clauses à imprimer : les actives, dans leur ordre, dont le texte est écrit.
 * Une clause sans corps n'est pas une section vide, c'est une section absente.
 */
export function printableContractClauses(clauses: ContractSettingsClause[]): ContractSettingsClause[] {
  return clauses
    .filter((clause) => clause.enabled && clause.body.trim() !== '')
    .sort((left, right) => left.order - right.order)
}

/**
 * Identifiant stable dérivé d'un titre de gabarit.
 *
 * Le serveur impose `^[a-z][a-z0-9_]{0,59}$`. La dérivation reste ici plutôt que
 * dans un champ libre : un identifiant saisi à la main diverge d'une saison à
 * l'autre, et c'est justement lui qui relie un gabarit à la clause composée.
 * Rend une chaîne vide si le titre ne donne rien d'exploitable : mieux vaut
 * refuser l'enregistrement que fabriquer une clé arbitraire.
 */
export function toContractClauseKey(title: string): string {
  const normalized = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return /^[a-z]/.test(normalized) ? normalized.slice(0, 60) : ''
}

/** Remonte ou descend un gabarit d'un rang, en renumérotant toute la liste. */
export function moveContractClauseTemplate(
  templates: ContractClauseTemplate[],
  key: string,
  direction: -1 | 1,
): ContractClauseTemplate[] {
  const ordered = [...templates].sort((left, right) => left.displayOrder - right.displayOrder)
  const index = ordered.findIndex((template) => template.key === key)
  const target = index + direction
  if (index === -1 || target < 0 || target >= ordered.length) return templates
  const moved = ordered[index]
  const displaced = ordered[target]
  if (!moved || !displaced) return templates
  ordered[index] = displaced
  ordered[target] = moved
  return ordered.map((template, position) => ({ ...template, displayOrder: position + 1 }))
}

/** Remonte ou descend une clause d'un rang, en renumérotant tout le tableau. */
export function moveContractClause(
  clauses: ContractSettingsClause[],
  key: string,
  direction: -1 | 1,
): ContractSettingsClause[] {
  const ordered = [...clauses].sort((left, right) => left.order - right.order)
  const index = ordered.findIndex((clause) => clause.key === key)
  const target = index + direction
  if (index === -1 || target < 0 || target >= ordered.length) return clauses
  const moved = ordered[index]
  const displaced = ordered[target]
  if (!moved || !displaced) return clauses
  ordered[index] = displaced
  ordered[target] = moved
  return ordered.map((clause, position) => ({ ...clause, order: position + 1 }))
}
